import { useEffect, useEffectEvent, useState } from 'react';

import type * as RealtimeAiM from 'client/services/ai/realtime';

import { getRealtimeAiClient } from 'services';
import { type ModeT, startAgent } from 'services/ai';

import {
  DEFAULT_VOICE,
  type ExperienceT,
  type SessionKindT,
  type TranscriptT,
  findIndexById,
  useGlobalState,
} from 'utils';

import { useSession } from './useSession';

export type StateT = 'FAILED' | 'INITIALIZATION' | 'LISTENING'
  | 'LISTENING-AND-HEARING' | 'SPEAKING' | 'THINKING';

type UseWellbeingAgentArgT = {
  experience: ExperienceT | null | undefined;
  mode: ModeT;
  onSignal?: RealtimeAiM.SignalListenerT;
  sessionKind: SessionKindT | null | undefined;
};

type UseWellbeingAgentResT = {
  noAudioInputAfterMessageId?: string;
  state: StateT;
  transcript: TranscriptT;
};

function noop() {
  // NOOP
}

// TODO: It needs some refactoring to make it easier to manage running the agent
// in different modes.
export function useWellbeingAgent({
  experience,
  mode,
  onSignal,
  sessionKind,
}: UseWellbeingAgentArgT): UseWellbeingAgentResT {
  const [voice] = useGlobalState('user.voice');

  const [state, setState] = useState<StateT>('INITIALIZATION');
  const [transcript, setTranscript] = useState<TranscriptT>([]);

  const [userId] = useGlobalState('user.id');
  if (!userId) throw Error('Not authenticated');

  const [, setShowAudioSetup] = useGlobalState('ui.audioSetup');
  const [, setAudioOutputBlocked] = useGlobalState('ui.audioOutputBlocked');
  const [, setMicrophoneLost] = useGlobalState('ui.microphoneLost');
  const [, setSessionDisconnected] = useGlobalState('ui.sessionDisconnected');

  const session = useSession();

  const onAgentStart = useEffectEvent(async (
    agentMode: ModeT,
    agentUserId: string,
  ) => startAgent(
    agentMode,
    voice ?? DEFAULT_VOICE,
    onSignal ?? noop,
    agentUserId,
    experience,
    session,
    sessionKind,
  ));

  const [
    noAudioInputAfterMessageId,
    setNoAudioInputAfterMessageId,
  ] = useState<string>();

  useEffect(() => {
    let a: typeof RealtimeAiM | undefined;

    // The dynamic import and the agent start both settle after an early unmount
    // could have run the cleanup below. Only the two checks below are needed:
    // stopping from here as well would queue behind the cleanup's own stop(),
    // and so land on the next page's session.
    let cancelled = false;
    const isCancelled = (): boolean => cancelled;

    // Clear any stale audio overlays from a previous page.
    setAudioOutputBlocked(false);
    setMicrophoneLost(false);
    setSessionDisconnected(false);

    void (async () => {
      a = await getRealtimeAiClient();
      if (isCancelled()) return;

      const agent = await onAgentStart(mode, userId);
      if (isCancelled()) return;

      if (agent.state) setState(agent.state);

      if (!agent.success) setState('FAILED');

      if (agent.failedMicrophoneAccess) setShowAudioSetup(true);

      // eslint-disable-next-line complexity
      const unsub = agent.emitter.addListener((event) => {
        switch (event.type) {
          case 'ABOUT_TO_STOP':
          case 'AUDIO_OUTPUT_FINISHED':
          case 'CLOSING_CHECK_STARTED':
          case 'LINE_FINISHED':
          case 'STARTED':
          case 'TOOL_CALLED':
          case 'USER_TURN':
            break;
          case 'AUDIO_NO_INPUT_WARNING': {
            if (!a) throw Error('Internal error');
            const t = a.getCurrentTranscript();
            const last = t[t.length - 1];
            setNoAudioInputAfterMessageId(last?.id);
          } break;
          case 'FAILED':
            setState('FAILED');
            break;
          case 'FAILED/AUDIO_OUTPUT':
            // iOS blocked gesture-less autoplay; surface the "Tap to enable
            // sound" overlay.
            setAudioOutputBlocked(true);
            break;
          case 'FAILED/DISCONNECTED':
            // The connection is gone, so the mic affordance no longer applies.
            setMicrophoneLost(false);
            setSessionDisconnected(true);
            break;
          case 'FAILED/MICROPHONE_ACCESS':
            setShowAudioSetup(true);
            // NOTE: I believe, there is no need to do anything else here:
            //
            // 1. Once the `audioSetup` flag is set in the global state,
            //    some parent component of the componet that rendered this
            //    useWellbeingAgent() hook will render <AudioSetup> component
            //    instead of the component tree that rendered this hook.
            //
            // 2. The previous point also means that once the <AudioSetup> is
            //    closed, the component tree with this hook will be rendered
            //    from scratch, thus automatically re-trying to start AI agent.
            break;
          case 'FAILED/MICROPHONE_LOST':
            // Re-acquiring needs a user gesture, so it falls to the UI.
            setMicrophoneLost(true);
            break;
          case 'MICROPHONE_RECOVERED':
            setMicrophoneLost(false);
            break;
          case 'NEW_STATE':
            setState(event.state);
            break;
          case 'NEW_TRANSCRIPT_ITEM':
            setTranscript((prev) => [...prev, { ...event.item }]);
            break;
          case 'STOPPED':
            unsub();
            break;
          case 'TRANSCRIPT_ITEM_REMOVED':
            setTranscript(
              (prev) => prev.filter((item) => item.id !== event.item.id),
            );
            break;
          case 'TRANSCRIPT_ITEM_UPDATED':
            setTranscript((prev) => {
              const res = [...prev];
              const idx = findIndexById(res, event.item.id);
              if (idx < 0) throw Error('Internal error');
              res[idx] = { ...event.item };
              return res;
            });
            break;
          default: throw Error('Unexpected event type');
        }
      });
    })();

    return () => {
      cancelled = true;
      setAudioOutputBlocked(false);
      setMicrophoneLost(false);
      setSessionDisconnected(false);
      void a?.stop();
    };
  }, [
    mode,
    setAudioOutputBlocked,
    setMicrophoneLost,
    setSessionDisconnected,
    setShowAudioSetup,
    userId,
  ]);

  return { noAudioInputAfterMessageId, state, transcript };
}
